<template>
  <div 
    v-if="isLoading && settingsLoaded" 
    class="page-loader-overlay"
    :style="{
      backgroundColor: loaderSettings.loader_bg_color || '#ffffff',
      color: loaderSettings.loader_text_color || '#333333'
    }"
  >
    <div class="page-loader-content">
      <!-- Logo (if available) -->
      <div v-if="loaderSettings.logo_url" class="loader-logo">
        <img 
          :src="getLogoUrl()" 
          :alt="'Logo'" 
          class="loader-logo-img"
        >
      </div>
      
      <!-- Loader Animation -->
      <div 
        v-if="loaderSettings.loader_style === 'wave'"
        class="loader-animation loader-wave"
        :style="{ color: loaderSettings.loader_color || '#3498db' }"
      >
        <div class="wave-bar" style="animation-delay: 0s;"></div>
        <div class="wave-bar" style="animation-delay: 0.1s;"></div>
        <div class="wave-bar" style="animation-delay: 0.2s;"></div>
        <div class="wave-bar" style="animation-delay: 0.3s;"></div>
        <div class="wave-bar" style="animation-delay: 0.4s;"></div>
      </div>
      <div 
        v-else
        class="loader-animation"
        :class="`loader-${loaderSettings.loader_style || 'spinner'}`"
        :style="{ color: loaderSettings.loader_color || '#3498db' }"
      ></div>
      
      <!-- Loader Text -->
      <div class="loader-text-content">
        <div class="loader-main-text">
          {{ loaderSettings.loader_text || 'Φόρτωση...' }}
        </div>
        <div v-if="loaderSettings.loader_subtitle" class="loader-subtitle">
          {{ loaderSettings.loader_subtitle }}
        </div>
      </div>
      
      <!-- Progress Bar (optional) -->
      <div v-if="showProgress" class="loader-progress-bar">
        <div 
          class="loader-progress-fill"
          :style="{ 
            width: progress + '%',
            backgroundColor: loaderSettings.loader_color || '#3498db'
          }"
        ></div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { API_BASE_URL } from '@/utils/config'
import { authFetch } from '@/utils/authFetch'

interface LoaderSettings {
  loader_enabled: string
  loader_style: string
  loader_text: string
  loader_subtitle: string
  loader_color: string
  loader_bg_color: string
  loader_text_color: string
  loader_duration: number
  logo_url: string
}

const props = defineProps<{
  showProgress?: boolean
  customDuration?: number
}>()

const isLoading = ref(false)
const settingsLoaded = ref(false)
const progress = ref(0)
const loaderSettings = ref<LoaderSettings>({
  loader_enabled: 'true',
  loader_style: 'spinner',
  loader_text: 'Φόρτωση...',
  loader_subtitle: '',
  loader_color: '#3498db',
  loader_bg_color: '#ffffff',
  loader_text_color: '#333333',
  loader_duration: 3000,
  logo_url: ''
})

// Load settings from API
const loadSettings = async () => {
  try {
    const response = await authFetch(`${API_BASE_URL}/settings.php`)
    const data = await response.json()
    
    if (data.success && data.data.content) {
      const content = data.data.content
      loaderSettings.value = {
        loader_enabled: content.loader_enabled || 'true',
        loader_style: content.loader_style || 'spinner',
        loader_text: content.loader_text || 'Φόρτωση...',
        loader_subtitle: content.loader_subtitle || '',
        loader_color: content.loader_color || '#3498db',
        loader_bg_color: content.loader_bg_color || '#ffffff',
        loader_text_color: content.loader_text_color || '#333333',
        loader_duration: content.loader_duration || 3000,
        logo_url: content.logo_url || ''
      }
    }
  } catch (error) {
  } finally {
    settingsLoaded.value = true
  }
}

// Get logo URL with proper base path
const getLogoUrl = () => {
  if (!loaderSettings.value.logo_url) return ''
  
  // If it's already a full URL, return as is
  if (loaderSettings.value.logo_url.startsWith('http')) {
    return loaderSettings.value.logo_url
  }
  
  // Otherwise, construct the path to backend uploads
  return `${API_BASE_URL.replace('/api', '')}/uploads/logos/${loaderSettings.value.logo_url}`
}

// Start loader
const startLoader = () => {
  if (!settingsLoaded.value) {
    // Wait for settings to load first
    setTimeout(() => startLoader(), 50)
    return
  }
  
  if (loaderSettings.value.loader_enabled !== 'true') {
    isLoading.value = false
    return
  }

  isLoading.value = true
  const duration = props.customDuration || loaderSettings.value.loader_duration

  if (props.showProgress) {
    // Animate progress bar
    const progressInterval = setInterval(() => {
      progress.value += 2
      if (progress.value >= 100) {
        clearInterval(progressInterval)
        setTimeout(() => {
          isLoading.value = false
        }, 200)
      }
    }, duration / 50)
  } else {
    // Simple timeout
    setTimeout(() => {
      isLoading.value = false
    }, duration)
  }
}

// Hide loader manually (for external control)
const hideLoader = () => {
  isLoading.value = false
}

// Expose methods for parent components
defineExpose({
  startLoader,
  hideLoader,
  isLoading: computed(() => isLoading.value)
})

onMounted(() => {
  loadSettings()
  // Don't auto-start loader - let parent component control it
})
</script>

<style scoped>
.page-loader-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
  backdrop-filter: blur(2px);
}

.page-loader-content {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.loader-logo {
  margin-bottom: 1.5rem;
}

.loader-logo-img {
  max-width: 120px;
  max-height: 80px;
  width: auto;
  height: auto;
  object-fit: contain;
}

.loader-animation {
  width: 50px;
  height: 50px;
  display: inline-block;
}

/* Spinner Loader */
.loader-spinner {
  border: 4px solid rgba(0,0,0,0.1);
  border-left: 4px solid currentColor;
  border-radius: 50%;
  animation: loader-spin 1s linear infinite;
}

/* Dots Loader */
.loader-dots {
  position: relative;
  width: 80px;
  height: 20px;
}

.loader-dots::before,
.loader-dots::after {
  content: '';
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 13px;
  height: 13px;
  border-radius: 50%;
  background: currentColor;
  animation: loader-dots 1.4s infinite ease-in-out both;
}

.loader-dots::before {
  left: 0;
}

.loader-dots::after {
  left: 35px;
  animation-delay: 0.7s;
}

/* Pulse Loader */
.loader-pulse {
  background: currentColor;
  border-radius: 50%;
  animation: loader-pulse 1s infinite ease-in-out;
}

/* Wave Loader */
.loader-wave {
  position: relative;
  width: 80px;
  height: 50px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 5px;
}

.loader-wave .wave-bar {
  width: 10px;
  background: currentColor;
  animation: loader-wave 1.2s infinite ease-in-out;
  border-radius: 2px;
}

/* Bounce Loader */
.loader-bounce {
  position: relative;
  width: 70px;
  height: 50px;
}

.loader-bounce::before,
.loader-bounce::after {
  content: '';
  position: absolute;
  top: 0;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: currentColor;
  animation: loader-bounce 2s infinite ease-in-out;
}

.loader-bounce::before {
  left: 0;
}

.loader-bounce::after {
  left: 26px;
  animation-delay: 1s;
}

/* Custom Loader */
.loader-custom {
  border: 4px solid transparent;
  border-top: 4px solid currentColor;
  border-bottom: 4px solid currentColor;
  border-radius: 50%;
  animation: loader-custom 1.5s linear infinite;
}

/* Loader Text */
.loader-text-content {
  margin-top: 1rem;
}

.loader-main-text {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.loader-subtitle {
  font-size: 0.9rem;
  opacity: 0.7;
}

/* Progress Bar */
.loader-progress-bar {
  width: 200px;
  height: 4px;
  background: rgba(0,0,0,0.1);
  border-radius: 2px;
  overflow: hidden;
  margin-top: 1rem;
}

.loader-progress-fill {
  height: 100%;
  border-radius: 2px;
  transition: width 0.3s ease;
}

/* Animations */
@keyframes loader-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes loader-dots {
  0%, 80%, 100% {
    transform: translateY(-50%) scale(0);
  }
  40% {
    transform: translateY(-50%) scale(1);
  }
}

@keyframes loader-pulse {
  0%, 100% {
    transform: scale(0);
    opacity: 1;
  }
  50% {
    transform: scale(1);
    opacity: 0.5;
  }
}

@keyframes loader-wave {
  0%, 40%, 100% {
    height: 10px;
  }
  20% {
    height: 40px;
  }
}

@keyframes loader-bounce {
  0%, 100% {
    transform: scale(0);
  }
  50% {
    transform: scale(1);
  }
}

@keyframes loader-custom {
  0% {
    transform: rotate(0deg) scale(1);
  }
  50% {
    transform: rotate(180deg) scale(0.8);
  }
  100% {
    transform: rotate(360deg) scale(1);
  }
}

.page-loader-content {
  position: relative;
  z-index: 2;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .loader-animation {
    width: 40px;
    height: 40px;
  }
  
  .loader-main-text {
    font-size: 1rem;
  }
  
  .loader-subtitle {
    font-size: 0.8rem;
  }
  
  .loader-progress-bar {
    width: 150px;
  }
}
</style>